import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PlayerProvider, usePlayer } from "../../context/PlayerContext";
import FeedPlayerOverlay from "../feed/FeedPlayerOverlay";

// PersistentOverlay — renders the single FeedPlayerOverlay instance
// shared by all player routes. Reads everything from PlayerContext so
// the same DOM tree (and crucially the same <video> element) survives
// across `/`, `/feed`, `/:username` navigations.
//
// Position: `fixed` covering the area below the sticky header. This
// keeps the overlay floating above the page body regardless of where
// in the DOM tree React renders it, and lets PlayerLayout structure
// `<Outlet/>` + `<PersistentOverlay/>` siblings without worrying about
// document flow.
//
// Only mounts when `isPlaying === true` AND a bundle has been pushed
// by the current page. Pages clear the bundle on unmount.
const PersistentOverlay = () => {
  const {
    videoRef, userPausedRef, failedIdsRef, exhaustedToastShownRef,
    isPlaying, currentVideoIndex, setCurrentVideoIndex, bundle,
  } = usePlayer();
  if (!isPlaying || !bundle) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-30 overflow-y-auto lg:overflow-hidden bg-black
                 top-[calc(env(safe-area-inset-top,0px)+3.5rem)]
                 sm:top-[calc(env(safe-area-inset-top,0px)+4rem)]"
      style={{
        // Header is `position:fixed; top:0` with
        // `paddingTop: env(safe-area-inset-top)` PLUS an inner `h-14`
        // (mobile) / `h-16` (sm+). The previous `top-14 sm:top-16`
        // shorthand ignored the safe-area inset, so on iPhone with the
        // notch the persistent overlay's content tucked behind the
        // header's padding — user saw the top hidden + a rubber-band
        // that snapped back. The arbitrary-value Tailwind classes
        // above match the header's full visible height including
        // notch.
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
      data-testid="persistent-overlay-wrap"
    >
      <FeedPlayerOverlay
        {...bundle}
        videoRef={videoRef}
        userPausedRef={userPausedRef}
        failedIdsRef={failedIdsRef}
        exhaustedToastShownRef={exhaustedToastShownRef}
        currentVideoIndex={currentVideoIndex}
        setCurrentVideoIndex={setCurrentVideoIndex}
      />
    </div>
  );
};

// PlayerLayout — react-router v6 layout route shared by `/`, `/feed`,
// `/:username`. Owns the PlayerProvider so playback state survives
// across route transitions within this layout.
const PlayerLayout = () => {
  const location = useLocation();
  return (
    <PlayerProvider>
      {/* Wrap Outlet in a keyed div so route transitions retrigger the
          fade-in animation. The persistent overlay below is a sibling
          and therefore unaffected — the floating player stays put while
          only the page chrome behind it fades in. */}
      <div key={location.pathname} className="route-fade-in">
        <Outlet />
      </div>
      <PersistentOverlay />
    </PlayerProvider>
  );
};

export default PlayerLayout;
